import log from 'loglevel';
import { readEnvValue } from '@/constants/EnvironmentConfig';
import awsconfig from '@/amplifyconfiguration.json';

/**
 * Centralized Environment Identification Utility
 *
 * This module provides a single source of truth for environment identification
 * across the entire application. All environment-related checks should use
 * these utilities instead of hardcoded string comparisons.
 */

/**
 * Standard environment identifiers used throughout the application
 */
export const ENVIRONMENT_NAMES = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
  TEST: 'test',
} as const;

/**
 * Type for valid environment names
 */
export type EnvironmentName =
  (typeof ENVIRONMENT_NAMES)[keyof typeof ENVIRONMENT_NAMES];

/**
 * Amplify environment name mappings
 */
export const AMPLIFY_ENV_MAPPINGS = {
  listuxdeva: ENVIRONMENT_NAMES.DEVELOPMENT,
  listuxstga: ENVIRONMENT_NAMES.STAGING,
  listuxprda: ENVIRONMENT_NAMES.PRODUCTION,
} as const;

/**
 * Get the current environment name
 * Uses multiple detection methods in order of priority:
 * 1. NODE_ENV environment variable
 * 2. Amplify OAuth domain detection
 * 3. Browser hostname detection
 * 4. Defaults to development
 */
export function getCurrentEnvironment(): EnvironmentName {
  // Check NODE_ENV first
  const nodeEnv = readEnvValue('NODE_ENV');
  if (isValidEnvironmentName(nodeEnv)) {
    return nodeEnv;
  }

  // Check Amplify OAuth domain
  const amplifyEnv = getEnvironmentFromAmplifyDomain();
  if (amplifyEnv) {
    return amplifyEnv;
  }

  // Check browser hostname (client-side only)
  if (typeof window !== 'undefined') {
    const hostnameEnv = getEnvironmentFromHostname();
    if (hostnameEnv) {
      return hostnameEnv;
    }
  }

  // Default to development
  return ENVIRONMENT_NAMES.DEVELOPMENT;
}

/**
 * Check if the current environment is development
 */
export function isDevelopmentEnvironment(): boolean {
  return getCurrentEnvironment() === ENVIRONMENT_NAMES.DEVELOPMENT;
}

/**
 * Check if the current environment is staging
 */
export function isStagingEnvironment(): boolean {
  return getCurrentEnvironment() === ENVIRONMENT_NAMES.STAGING;
}

/**
 * Check if the current environment is production
 */
export function isProductionEnvironment(): boolean {
  return getCurrentEnvironment() === ENVIRONMENT_NAMES.PRODUCTION;
}

/**
 * Check if the current environment is test
 */
export function isTestEnvironment(): boolean {
  return getCurrentEnvironment() === ENVIRONMENT_NAMES.TEST;
}

/**
 * Check if running in development mode (development or test)
 */
export function isDevelopmentMode(): boolean {
  const env = getCurrentEnvironment();
  return (
    env === ENVIRONMENT_NAMES.DEVELOPMENT || env === ENVIRONMENT_NAMES.TEST
  );
}

/**
 * Get environment-specific configuration values
 */
export function getEnvironmentConfig() {
  const env = getCurrentEnvironment();

  return {
    environment: env,
    isDevelopment: env === ENVIRONMENT_NAMES.DEVELOPMENT,
    isStaging: env === ENVIRONMENT_NAMES.STAGING,
    isProduction: env === ENVIRONMENT_NAMES.PRODUCTION,
    isTest: env === ENVIRONMENT_NAMES.TEST,
    isDevelopmentMode: isDevelopmentMode(),
  };
}

/**
 * Validate if a string is a valid environment name
 */
export function isValidEnvironmentName(
  value: unknown
): value is EnvironmentName {
  return (
    typeof value === 'string' &&
    Object.values(ENVIRONMENT_NAMES).includes(value as EnvironmentName)
  );
}

/**
 * Get environment from Amplify OAuth domain
 */
function getEnvironmentFromAmplifyDomain(): EnvironmentName | null {
  try {
    const amplifyConfig = awsconfig as Record<string, unknown>;
    const oauth = amplifyConfig.oauth as Record<string, unknown> | undefined;
    const domain = oauth?.domain as string | undefined;

    if (!domain) return null;

    // Check for Amplify environment names in domain
    for (const [amplifyEnv, standardEnv] of Object.entries(
      AMPLIFY_ENV_MAPPINGS
    )) {
      if (domain.includes(amplifyEnv)) {
        return standardEnv;
      }
    }

    return null;
  } catch (error) {
    log.warn('Failed to determine environment from Amplify domain:', error);
    return null;
  }
}

/**
 * Get environment from browser hostname (client-side only)
 */
function getEnvironmentFromHostname(): EnvironmentName | null {
  if (typeof window === 'undefined') return null;

  try {
    const hostname = window.location.hostname;

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return ENVIRONMENT_NAMES.DEVELOPMENT;
    }

    // Subdomain-based detection
    if (hostname.includes('dev.')) return ENVIRONMENT_NAMES.DEVELOPMENT;
    if (hostname.includes('staging.')) return ENVIRONMENT_NAMES.STAGING;
    if (hostname.includes('prod.')) return ENVIRONMENT_NAMES.PRODUCTION;

    return null;
  } catch (error) {
    log.warn('Failed to determine environment from hostname:', error);
    return null;
  }
}
